import React, { useCallback, useEffect, useId, useState } from 'react';
import { useField, useForm } from 'react-final-form';
import { styled } from '@linaria/react';

import { FieldMeta, UserPreCheck } from '@blocks/types';

import { api } from '../../api';
import {
  ERROR_EMAIL_EMPTY,
  ERROR_EMAIL_INVALID,
  LOGOUT_EVENT,
  USER_ROLE_CUSTOMER,
} from '../../constants';
import { useApi } from '../../hooks';
import { FieldSubmitError, Input, Label, LogoutButton } from '../../ui';
import { Mail, Spinner, SuccessCheckmark } from '../../ui/icon';
import { composeValidators } from '../../utils';
import { email, isSubmitError, required } from '../../utils/validators';
import { AuthProps } from '..';

const isRequired = required(ERROR_EMAIL_EMPTY);
const isEmail = email(ERROR_EMAIL_INVALID);

const LoginField = styled.div`
  position: relative;
  & > input:not(input:disabled):focus + div,
  & > input:not(input:disabled):active + div,
  & > input:not(input:disabled):hover + div {
    path {
      fill: var(--bb-icon-color--hover);
    }
  }

  & .tooltip {
    padding: 8px;
    background-color: rgba(0, 0, 0, 0.8);

    b {
      display: block;
      margin-bottom: 8px;
    }

    ul {
      margin: 0;
      padding: 0 0 0 14px;
      line-height: 18px;
    }
  }
`;

const LoginSuccessCheckmarkContainerWritingAuth = styled.div`
  position: absolute;
  user-select: none;
  top: 50%;
  right: var(--bb-field-padding-left);
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  & svg {
    pointer-events: none;
  }
`;

const LoginCheck = styled.div`
  position: absolute;
  top: 50%;
  right: 0;
  display: flex;
  background-color: transparent;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  border-radius: 0 3px 3px 0;
  margin-top: -22.5px;

  & svg {
    outline: none;
  }
`;

const MailIconContainerAuth = styled.div<{ meta: FieldMeta }>`
  display: block;
  outline: none;
  background-color: transparent;
  border: none;
  padding: 0;
  margin: 0;
  position: absolute;
  left: 0;
  top: 50%;
  width: 43px;
  height: 43px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  border-radius: 0 var(--bb-field-border-radius) var(--bb-field-border-radius) 0;
  path {
    fill: ${({ meta }) => (isSubmitError(meta) ? 'var(--bb-error-color)' : 'var(--bb-icon-color)')};
  }
`;

/**
 * @TODO fix overflow long logins (padding for LoginCheck)
 */
type LoginProps = { authenticated: boolean; remote: boolean; logoutHandler: () => void } & Pick<
  AuthProps,
  'domain' | 'withIcon'
>;

const Login: React.FC<LoginProps> = ({
  domain,
  remote,
  authenticated,
  logoutHandler,
  withIcon,
}) => {
  const { mutators } = useForm();
  const { input, meta } = useField('login', {
    validate: composeValidators(isRequired, isEmail),
  });

  const [checking, setChecking] = useState(false);
  const [existingUser, setExistingUser] = useState(false);
  const [loginCached, setLogin] = useState();

  const {
    body,
    meta: userCheckMeta,
    request,
  } = useApi<UserPreCheck>(
    api(domain, 'userCheck'),
    {
      params: {
        login: input.value,
      },
    },
    false,
  );

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (meta.valid && input.value && loginCached !== input.value) {
      setChecking(true);
      setLogin(input.value);
      request();
    }

    input.onBlur(e);
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (existingUser) {
      setExistingUser(false);
    }
    mutators.toggleGdpr(false);
    mutators.setUserRole(USER_ROLE_CUSTOMER);
    mutators.clearSubmitError();

    setLogin(undefined);
    input.onChange(e);
  };

  useEffect(() => {
    const cleanup = () => {};

    if (!body) return cleanup;

    const {
      marketing_agree_required,
      login_ability,
      policy_agree_required,
      register_ability,
      user_role,
    } = body;

    if (!register_ability) {
      mutators.toggleGdpr(policy_agree_required || marketing_agree_required);
    }
    setExistingUser(login_ability);
    mutators.setUserRole(user_role);
    setChecking(false);

    return cleanup;
  }, [JSON.stringify(userCheckMeta)]);

  const logout = () => {
    logoutHandler();
  };

  const handleLogout = useCallback(() => {
    mutators.removeLogin();
    mutators.changeLoginAbility(false);
  }, [mutators]);

  useEffect(() => {
    document.addEventListener(LOGOUT_EVENT, handleLogout);
    return () => {
      document.removeEventListener(LOGOUT_EVENT, handleLogout);
    };
  }, [handleLogout]);
  const id = useId();
  return (
    <>
      <Label htmlFor={id}>Email</Label>
      <LoginField>
        <Input
          id={id}
          placeholder="Enter your email"
          {...input}
          onBlur={onBlur}
          onChange={onChange}
          disabled={remote || authenticated}
          meta={meta}
          validate="submit"
          autoFocus
          style={{
            paddingLeft: withIcon ? '40px' : 'var(--bb-field-padding-left)',
          }}
        />
        {withIcon && (
          <MailIconContainerAuth meta={meta}>
            <Mail />
          </MailIconContainerAuth>
        )}
        {authenticated ? (
          <LogoutButton
            type="button"
            onClick={logout}
            title="Log out"
            tabIndex="-1"
          />
        ) : (
          <>
            <LoginCheck>
              {checking && (
                <Spinner
                  width="24"
                  height="24"
                />
              )}
            </LoginCheck>
            {(existingUser || remote) && (
              <LoginSuccessCheckmarkContainerWritingAuth>
                <SuccessCheckmark />
              </LoginSuccessCheckmarkContainerWritingAuth>
            )}
          </>
        )}
      </LoginField>

      <FieldSubmitError meta={meta} />
    </>
  );
};

export default React.memo(Login);
